import api from './api';
import { ventasService } from './ventasService';
import { reportesService } from './reportesService';
import { inventarioService } from './inventarioService';
import { productosService } from './productosService';
import { clientesService } from './clientesService';

/**
 * Servicio para obtener datos del dashboard
 */
export const dashboardService = {
  /**
   * Obtener estadísticas generales del dashboard
   * @param {number} sucursalId - ID de la sucursal (opcional)
   * @param {number} empresaId - ID de la empresa (opcional)
   */
  getEstadisticas: async (sucursalId = null, empresaId = null) => {
    try {
      // Obtener fecha de inicio y fin del mes actual
      const ahora = new Date();
      const fechaInicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
      // Último día del mes actual (día 0 del mes siguiente)
      const fechaFinMes = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0, 23, 59, 59);

      // Obtener ventas del mes actual
      const ventasResponse = await ventasService.getAll(
        sucursalId,
        null,
        fechaInicioMes.toISOString().split('T')[0],
        fechaFinMes.toISOString().split('T')[0]
      );

      const ventas = (ventasResponse.success && ventasResponse.data) ? (Array.isArray(ventasResponse.data) ? ventasResponse.data : []) : [];
      
      const totalVentasMes = ventas.length;
      
      // Filtrar solo ventas completadas para el total de ingresos
      const ventasCompletadas = ventas.filter(v => {
        const estatus = (v.Estatus || v.estatus || '').toLowerCase();
        return estatus === 'completada';
      });
      
      const totalIngresosMes = ventasCompletadas.reduce((sum, v) => {
        const total = parseFloat(v.Total || v.total || 0);
        return sum + (isNaN(total) ? 0 : total);
      }, 0);

      // Obtener productos activos
      const productosResponse = await productosService.getAll(empresaId, sucursalId);
      const productosData = (productosResponse.success && productosResponse.data) ? (Array.isArray(productosResponse.data) ? productosResponse.data : []) : [];
      
      // Filtrar productos activos: Activo debe ser true, 1, o no estar definido (por defecto true)
      const productosActivos = productosData.filter(p => {
        const activo = p.Activo !== undefined ? p.Activo : (p.activo !== undefined ? p.activo : true);
        const esActivo = activo === true || activo === 1 || activo === 'true' || activo === '1';
        return esActivo;
      }).length;

      // Obtener clientes activos
      const clientesResponse = await clientesService.getAll(empresaId, sucursalId);
      const clientesData = (clientesResponse.success && clientesResponse.data) ? (Array.isArray(clientesResponse.data) ? clientesResponse.data : []) : [];
      
      // Filtrar clientes activos: Activo debe ser true, 1, o no estar definido (por defecto true)
      const clientesActivos = clientesData.filter(c => {
        const activo = c.Activo !== undefined ? c.Activo : (c.activo !== undefined ? c.activo : true);
        const esActivo = activo === true || activo === 1 || activo === 'true' || activo === '1';
        return esActivo;
      }).length;

      // Obtener productos con stock bajo (manejar error si falla)
      let cantidadStockBajo = 0;
      try {
        const stockBajoResponse = await inventarioService.getStockBajo(sucursalId).catch(() => {
          return { success: false, data: [] };
        });
        const productosStockBajo = (stockBajoResponse && stockBajoResponse.success && stockBajoResponse.data) ? (Array.isArray(stockBajoResponse.data) ? stockBajoResponse.data : []) : [];
        cantidadStockBajo = productosStockBajo.length;
      } catch (error) {
        // Continuar con 0 si falla
      }

      const resultado = {
        success: true,
        data: {
          totalVentasMes,
          totalIngresosMes,
          productosActivos,
          clientesActivos,
          productosStockBajo: cantidadStockBajo,
        },
      };
      
      return resultado;
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error al obtener estadísticas',
        data: {
          totalVentasMes: 0,
          totalIngresosMes: 0,
          productosActivos: 0,
          clientesActivos: 0,
          productosStockBajo: 0,
        },
      };
    }
  },

  /**
   * Obtener datos de ventas por mes para gráfica
   * @param {number} sucursalId - ID de la sucursal (opcional)
   * @param {number} empresaId - ID de la empresa (opcional)
   */
  getVentasPorMes: async (sucursalId = null, empresaId = null) => {
    try {
      // Obtener datos de los últimos 6 meses
      const ahora = new Date();
      const meses = [];
      const datos = [];

      for (let i = 5; i >= 0; i--) {
        const fecha = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1);
        const fechaInicio = new Date(fecha.getFullYear(), fecha.getMonth(), 1);
        const fechaFin = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0, 23, 59, 59);

        const mesesNombres = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
        meses.push(mesesNombres[fecha.getMonth()]);

        const ventasResponse = await ventasService.getAll(
          sucursalId,
          null,
          fechaInicio.toISOString().split('T')[0],
          fechaFin.toISOString().split('T')[0]
        );

        const ventas = (ventasResponse.success && ventasResponse.data) ? (Array.isArray(ventasResponse.data) ? ventasResponse.data : []) : [];
        
        // Filtrar solo ventas completadas para el total
        const ventasCompletadas = ventas.filter(v => {
          const estatus = (v.Estatus || v.estatus || '').toLowerCase();
          return estatus === 'completada';
        });
        
        const total = ventasCompletadas.reduce((sum, v) => {
          const totalVenta = parseFloat(v.Total || v.total || 0);
          return sum + (isNaN(totalVenta) ? 0 : totalVenta);
        }, 0);
        datos.push(total);
      }

      return {
        success: true,
        data: {
          labels: meses,
          datos: datos,
        },
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error al obtener ventas por mes',
      };
    }
  },

  /**
   * Obtener ventas recientes
   * @param {number} sucursalId - ID de la sucursal (opcional)
   * @param {number} limite - Número de ventas a obtener (default: 5)
   */
  getVentasRecientes: async (sucursalId = null, limite = 5) => {
    try {
      const ventasResponse = await ventasService.getAll(sucursalId);
      const ventas = (ventasResponse.success && ventasResponse.data) ? (Array.isArray(ventasResponse.data) ? ventasResponse.data : []) : [];
      
      // Ordenar por fecha más reciente y tomar las primeras
      const ventasOrdenadas = ventas
        .sort((a, b) => {
          const fechaA = new Date(a.FechaVenta || a.fechaVenta || 0);
          const fechaB = new Date(b.FechaVenta || b.fechaVenta || 0);
          return fechaB - fechaA;
        })
        .slice(0, limite);

      return {
        success: true,
        data: ventasOrdenadas,
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error al obtener ventas recientes',
      };
    }
  },

  /**
   * Obtener productos con stock bajo
   * @param {number} sucursalId - ID de la sucursal (opcional)
   * @param {number} limite - Número de productos a obtener (default: 5)
   */
  getProductosStockBajo: async (sucursalId = null, limite = 5) => {
    try {
      const stockBajoResponse = await inventarioService.getStockBajo(sucursalId);
      const productos = (stockBajoResponse.success && stockBajoResponse.data) ? (Array.isArray(stockBajoResponse.data) ? stockBajoResponse.data : []) : [];
      
      return {
        success: true,
        data: productos.slice(0, limite),
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error al obtener productos con stock bajo',
      };
    }
  },
};

